/**
 * Buyer temperature — float 0..4 rendered as a 4-segment bar plus a status
 * label. See brief §5.2. Temperature deltas are applied after the persona
 * responds; at Checkpoint C they'll come from `clientData.temperature_delta`.
 * This module is pure so it can be unit-tested independently.
 */

export type TemperatureStatus = 'cold' | 'listening' | 'warming' | 'engaged' | 'ready_to_close';

export interface TemperatureView {
  value: number;
  clampedValue: number;
  fraction: number;
  status: TemperatureStatus;
  label: string;
}

export function readTemperature(value: number): TemperatureView {
  const clampedValue = clamp(value, 0, 4);
  const status = statusFor(clampedValue);
  return {
    value,
    clampedValue,
    fraction: clampedValue / 4,
    status,
    label: labelFor(status),
  };
}

export function applyDelta(current: number, delta: number): number {
  return clamp(current + delta, 0, 4);
}

function statusFor(v: number): TemperatureStatus {
  if (v < 1.0) return 'cold';
  if (v < 2.0) return 'listening';
  if (v < 3.0) return 'warming';
  if (v < 3.8) return 'engaged';
  return 'ready_to_close';
}

function labelFor(s: TemperatureStatus): string {
  switch (s) {
    case 'cold':
      return 'COLD';
    case 'listening':
      return 'LISTENING';
    case 'warming':
      return 'WARMING';
    case 'engaged':
      return 'ENGAGED';
    case 'ready_to_close':
      return 'READY TO CLOSE';
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
